import { ReactNode } from 'react';
import ConsumerHeader from "@/components/ConsumerHeader";
import ProviderBrandedHeader from "@/components/ProviderBrandedHeader";
import SearchModal from "@/components/SearchModal";
import ProfileCompletionPrompt from "@/components/ProfileCompletionPrompt";

interface BrandStyles {
  backgroundColor?: string | null;
  font?: string | null;
  headingColor?: string | null;
  bodyColor?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

interface Provider {
  id: string;
  name: string;
  display_name?: string | null;
  image_url?: string | null;
  slug?: string;
  brand_primary_color?: string | null;
  brand_secondary_color?: string | null;
  brand_font?: string | null;
  brand_header_icon_text_color?: string | null;
  brand_header_button_text_color?: string | null;
  brand_header_letter_spacing?: string | null;
}

interface BookingLayoutProps {
  children: ReactNode;
  searchModalOpen: boolean;
  setSearchModalOpen: (open: boolean) => void;
  showProfilePrompt: boolean;
  onProfileCompletion: () => void;
  onProfileCancel: () => void;
  isProviderViewing: boolean;
  brandStyles?: BrandStyles;
  provider?: Provider | null;
}

const BookingLayout = ({
  children,
  searchModalOpen,
  setSearchModalOpen,
  showProfilePrompt,
  onProfileCompletion,
  onProfileCancel,
  isProviderViewing,
  brandStyles,
  provider
}: BookingLayoutProps) => {
  const hasBranding = provider?.brand_primary_color;
  
  const containerStyle: React.CSSProperties = {
    backgroundColor: brandStyles?.backgroundColor || undefined,
    fontFamily: brandStyles?.font || undefined,
    ['--brand-heading-color' as any]: brandStyles?.headingColor || undefined,
    ['--brand-body-color' as any]: brandStyles?.bodyColor || undefined,
    ['--brand-primary-color' as any]: brandStyles?.primaryColor || undefined,
  };

  return (
    <div 
      className={`min-h-screen ${!brandStyles?.backgroundColor ? 'bg-gray-50' : ''}`} 
      style={containerStyle}
      data-brand-page
    >
      {/* Override default purple accent/secondary colors with neutral grays */}
      <style>{`
        [data-brand-page] {
          --accent: 0 0% 95%;
          --accent-foreground: 0 0% 10%;
          --secondary: 0 0% 96%;
          --secondary-foreground: 0 0% 10%;
        }
        
        /* Override any remaining purple badge backgrounds */
        [data-brand-page] .bg-secondary {
          background-color: hsl(0 0% 92%) !important;
        }
        [data-brand-page] .bg-accent {
          background-color: hsl(0 0% 95%) !important;
        }
      `}</style>
      
      {/* Inject brand color styles */}
      {brandStyles?.headingColor && (
        <style>{`
          [data-brand-page] h1, [data-brand-page] h2, [data-brand-page] h3, [data-brand-page] h4 {
            color: ${brandStyles.headingColor} !important;
          }
        `}</style>
      )}

      {brandStyles?.primaryColor && (
        <style>{`
          /* Primary surfaces */
          [data-brand-page] .bg-primary {
            background-color: ${brandStyles.primaryColor} !important;
          }
          [data-brand-page] .border-primary {
            border-color: ${brandStyles.primaryColor} !important;
          }
          [data-brand-page] .text-primary {
            color: ${brandStyles.primaryColor} !important;
          }

          /* Ensure readable text on primary background (buttons, selected calendar days) */
          [data-brand-page] .text-primary-foreground {
            color: #ffffff !important;
          }

          /* Make outline buttons use brand color for border/text */
          [data-brand-page] button.border-input {
            border-color: ${brandStyles.primaryColor} !important;
          }
          [data-brand-page] button.border-input:not(.bg-primary) {
            color: ${brandStyles.primaryColor} !important;
          }
        `}</style>
      )}

      {/* Use branded header if provider has branding, otherwise use default */}
      {brandStyles?.primaryColor && provider ? (
        <ProviderBrandedHeader provider={provider} />
      ) : (
        <ConsumerHeader />
      )}
      
      <SearchModal open={searchModalOpen} onOpenChange={setSearchModalOpen} />
      
      {/* Profile Completion Prompt - blocking overlay */}
      {showProfilePrompt && <ProfileCompletionPrompt onComplete={onProfileCompletion} onCancel={onProfileCancel} />}
      
      {/* Main booking content - only show if profile is complete */}
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${showProfilePrompt ? 'pointer-events-none opacity-50' : ''}`}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Book Your Session</h1>
          <p className="text-gray-600">Complete your booking in just a few steps</p>
          {isProviderViewing && (
            <div className="mt-2 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full inline-block">
              Provider View - Showing all available slots
            </div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
};

export default BookingLayout;